import React, { useState } from 'react';
import { motion } from 'framer-motion';

export const HeroSection: React.FC = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Animation settings for BedWoman image
  const bedWomanAnimationDuration = 2.0; // Duration in seconds

  return (
    <motion.section
      className="relative min-h-screen overflow-hidden flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        type: 'spring',
        stiffness: 50,
        damping: 20,
        mass: 1,
      }}
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {!videoError ? (
          <>
            <video
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                videoLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => setVideoLoaded(true)}
              onError={() => setVideoError(true)}
            >
              <source src="/lightsleep_bg.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </>
        ) : (
          /* Fallback gradient if video fails to load */
          <div className="absolute inset-0 bg-gradient-to-br from-l-bg-2 to-l-bg-3 dark:from-d-bg-2 dark:to-d-bg-3" />
        )}
      </div>

      {/* Hero Content with relative positioning for image container */}
      <div className="relative z-10 flex-grow">
        {/* Image container with relative positioning */}
        {/* Mobile Layout - Text centered on screen with image at bottom */}
        <div className="block md:hidden w-full h-full flex flex-col min-h-screen">
          <div className="flex-grow flex flex-col justify-center items-center px-4">
            <div className="text-center w-fit">
              <motion.h1
                className="text-9xl font-sawarabi text-l-text-1 mb-4 drop-shadow-2xl"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 80,
                  damping: 15,
                  mass: 1,
                  delay: 0.6,
                }}
              >
                光眠
              </motion.h1>
              <motion.p
                className="text-2xl text-l-text-1/80 mb-8 drop-shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 80,
                  damping: 15,
                  mass: 1,
                  delay: 0.8,
                }}
              >
                あなた仕様の光マットレスで
              </motion.p>
              <motion.div
                className="flex flex-col gap-4 justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 80,
                  damping: 15,
                  mass: 1,
                  delay: 1.0,
                }}
              >
                <motion.button
                  onClick={() =>
                    document
                      .getElementById('about')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="px-8 py-3 bg-white/70 text-l-text-1 text-lg rounded-full backdrop-blur shadow-2xl font-medium drop-shadow-lg"
                  whileHover={{
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    scale: 1.05,
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 25,
                    mass: 0.5,
                  }}
                >
                  おすすめ診断
                </motion.button>
              </motion.div>
            </div>
          </div>

          {/* Image at bottom - mobile */}
          <motion.img
            src="/BedWoman.webp"
            alt="Woman sleeping peacefully"
            className="w-full h-auto object-cover"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              type: 'spring',
              stiffness: 60,
              damping: 20,
              mass: 1,
              delay: 0,
              duration: bedWomanAnimationDuration,
            }}
          />
        </div>

        {/* Desktop Layout - Text above image */}
        <div className="hidden md:flex w-full h-full flex-col justify-end items-center min-h-screen">
          {/* Text Content positioned naturally above image */}
          <div className="text-center w-fit mb-6">
            <motion.h1
              className="text-9xl lg:text-[210px] font-sawarabi text-l-text-1 mb-4 drop-shadow-2xl"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                stiffness: 80,
                damping: 15,
                mass: 1,
                delay: 0.6,
              }}
            >
              光眠
            </motion.h1>
            <motion.p
              className="text-2xl lg:text-4xl text-l-text-1/80 mb-8 drop-shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                stiffness: 80,
                damping: 15,
                mass: 1,
                delay: 0.8,
              }}
            >
              あなた仕様の光マットレスで
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-end"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                stiffness: 80,
                damping: 15,
                mass: 1,
                delay: 1.0,
              }}
            >
              <motion.button
                onClick={() =>
                  document
                    .getElementById('about')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="px-8 py-3 bg-white/70 text-l-text-1 text-lg rounded-full backdrop-blur shadow-2xl font-medium drop-shadow-lg"
                whileHover={{
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  scale: 1.05,
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                  mass: 0.5,
                }}
              >
                おすすめ診断
              </motion.button>
            </motion.div>
          </div>

          {/* Image at bottom - with right spacing on desktop */}
          <motion.img
            src="/BedWoman.webp"
            alt="Woman sleeping peacefully"
            className="w-full h-auto object-cover pr-8 lg:pr-16"
            initial={{ opacity: 0, y: 300 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              type: 'spring',
              stiffness: 60,
              damping: 20,
              mass: 1,
              delay: 0,
              duration: bedWomanAnimationDuration,
            }}
          />
        </div>
      </div>
    </motion.section>
  );
};
